import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import FeatureReductionCluster from '@arcgis/core/layers/support/FeatureReductionCluster';
import ClassBreaksRenderer from '@arcgis/core/renderers/ClassBreaksRenderer';
import SimpleMarkerSymbol from '@arcgis/core/symbols/SimpleMarkerSymbol';
import {
  finalize,
  from,
  map,
  mergeMap,
  Observable,
  of,
  Subject,
  Subscription,
  tap,
  zip,
} from 'rxjs';
import { LoadingController } from 'app/shared/helpers/loading.controller';
import { MainMapService } from 'app/shared/components/main-map/main-map.service';
import { ColorUtils } from 'app/shared/utils/color.utils';
import { EsriUtils } from 'app/shared/utils/esri.utils';
import Field from '@arcgis/core/layers/support/Field';
import { AppConfigService } from 'app/shared/services/app-config.service';
import { LayersInfoService } from '../../layers.service';
import { TranslationService } from '@services/translation.service';

@Component({
  selector: 'app-cluster-layers',
  templateUrl: 'cluster-layers.component.html',
  styleUrls: ['cluster-layers.component.scss'],
})
export class ClusterLayersComponent implements OnChanges, OnDestroy {
  @Input()
  mapView: __esri.MapView;
  @Input()
  manipulationGroupLayer: __esri.GroupLayer;

  loadingController = new LoadingController();

  onLayerEnabledSubject = new Subject<any>();

  private announcementTOCLayerUrl = this.appConfig.normalizeUrl(
    '~announcementTOCService'
  );
  private registerObjectsTOCLayerUrl = this.appConfig.normalizeUrl(
    '~registerObjectsService'
  );

  private mapViewSubscription: Subscription;

  constructor(
    private appConfig: AppConfigService,
    private mainMapService: MainMapService,
    private layersInfoService: LayersInfoService,
    public translationService: TranslationService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.mapView || changes.manipulationGroupLayer) {
      this.handleMapView();
    }
  }

  ngOnDestroy(): void {
    this.mapViewSubscription?.unsubscribe();
  }

  allowLayerFn = (
    layer: __esri.FeatureLayer | __esri.Sublayer
  ): Observable<boolean> => {
    const layerId =
      (layer as __esri.FeatureLayer).layerId ?? (layer.id as number);
    const layerUrl = EsriUtils.resolveLayerUrl(layer.url, layerId);

    let allowLayerObservable: Observable<boolean>;
    if (
      layerUrl.startsWith(this.announcementTOCLayerUrl) ||
      layerUrl.startsWith(this.registerObjectsTOCLayerUrl)
    ) {
      allowLayerObservable = of(true);
    } else {
      let geomTypeObservable: Observable<string>;
      if (layer instanceof FeatureLayer) {
        geomTypeObservable = EsriUtils.load(layer).pipe(
          map(() => layer.geometryType)
        );
      } else {
        geomTypeObservable = this.layersInfoService.getLayerGeomType(layerUrl);
      }

      allowLayerObservable = geomTypeObservable.pipe(
        map((geometryType) => {
          return !!geometryType?.toLowerCase()?.includes('point');
        })
      );
    }

    return allowLayerObservable;
  };

  private handleMapView() {
    this.mapViewSubscription?.unsubscribe();
    if (!this.mapView) {
      return;
    }

    const subscription = new Subscription();

    const groupLayer: __esri.LayersMixin =
      this.manipulationGroupLayer ?? this.mapView.map;

    let layer: __esri.FeatureLayer;
    let layerSubscription: Subscription;

    let mainImageLayerShouldInvisible = false;

    const handleMainImageLayerVisible = (shouldEnableEffect = false) => {
      if (!mainImageLayerShouldInvisible && shouldEnableEffect) {
        mainImageLayerShouldInvisible = true;
        this.mainMapService.hideMainImageLayer();
      } else if (mainImageLayerShouldInvisible && !shouldEnableEffect) {
        mainImageLayerShouldInvisible = false;
        this.mainMapService.showMainImageLayer();
      }
    };

    const doDisable = () => {
      if (layer) {
        layerSubscription?.unsubscribe();
        this.loadingController.hide();
      }

      handleMainImageLayerVisible();
    };

    let currentUrl: string;
    subscription.add(
      this.onLayerEnabledSubject.subscribe((e) => {
        if (e) {
          const sourceLayer: __esri.FeatureLayer | __esri.Sublayer =
            e.layerItem.data.layer;
          const layerId: number =
            (sourceLayer as any).layerId ?? sourceLayer.id;
          let url = EsriUtils.resolveLayerUrl(sourceLayer.url, layerId);

          if (url.startsWith(this.announcementTOCLayerUrl)) {
            url = this.appConfig.normalizeUrl('~announcementCentroidService');
            url = EsriUtils.resolveLayerUrl(url, layerId);
          } else if (
            url.startsWith(this.registerObjectsTOCLayerUrl) &&
            [8, 9, 10].includes(layerId)
          ) {
            url = this.appConfig.normalizeUrl(
              '~registerObjectsMiddlePointService'
            );
            url = EsriUtils.resolveLayerUrl(url, layerId);
          }

          if (!layer || currentUrl !== url) {
            doDisable();
            currentUrl = url;

            const createSymbol = (
              color: string,
              size: number,
              outlineWidth: number
            ) => {
              const outlineColor = ColorUtils.alpha(color, 0.5).rgba();
              return new SimpleMarkerSymbol({
                style: 'circle',
                size,
                color,
                outline: {
                  color: outlineColor,
                  width: outlineWidth,
                },
              });
            };

            const smallSymbol = createSymbol('rgb(0, 128, 0)', 8, 5);
            const renderer = new ClassBreaksRenderer({
              defaultSymbol: smallSymbol,
              field: 'cluster_count',
            });

            const mediumSymbol = createSymbol('rgb(255, 128, 0)', 40, 6);
            const largeSymbol = createSymbol('rgb(192, 0, 0)', 50, 7);

            renderer.addClassBreakInfo(10, 25, mediumSymbol);
            renderer.addClassBreakInfo(
              26,
              Number.MAX_SAFE_INTEGER,
              largeSymbol
            );

            layer = new FeatureLayer({
              url,
              renderer,
              maxScale: 0,
              minScale: 300000,
            });

            this.loadingController.show();
            layerSubscription = new Subscription(() => {
              groupLayer.remove(layer);
              layer.destroy();
              layer = null;
            });
            layerSubscription.add(
              EsriUtils.load(layer)
                .pipe(
                  tap(() => {
                    layer.fields.push(
                      new Field({
                        name: 'cluster_count',
                        type: 'integer',
                      })
                    );

                    groupLayer.add(layer);
                  }),
                  mergeMap(() => {
                    return from(this.mapView.whenLayerView(layer));
                  }),
                  finalize(() => {
                    this.loadingController.hide();
                  })
                )
                .subscribe()
            );
          }

          layer.featureReduction = new FeatureReductionCluster({
            labelingInfo: [
              {
                deconflictionStrategy: 'none',
                labelExpressionInfo: {
                  expression: "Text($feature.cluster_count, '#,###')",
                },
                symbol: {
                  type: 'text',
                  color: '#ffffff',
                  font: {
                    weight: 'bold',
                    family: 'Noto Sans',
                    size: '12px',
                  },
                },
                labelPlacement: 'center-center',
              },
            ],
            clusterRadius: e.distance,
          });

          handleMainImageLayerVisible(true);
        } else {
          doDisable();
        }
      })
    );

    subscription.add(() => {
      doDisable();
    });

    this.mapViewSubscription = subscription;
  }
}
function ngOnDestroy() {
  throw new Error('Function not implemented.');
}

